import { HttpInterceptorFn } from '@angular/common/http';
import { ApplicationConfig } from '@angular/core';
import {
    PreloadAllModules,
    provideRouter,
    Routes,
    withComponentInputBinding,
    withPreloading,
} from '@angular/router';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { provideAmplitude } from 'shared/core/data-access/analytics';
import { provideApi } from 'shared/core/data-access/api';
import { provideAuth } from 'shared/core/data-access/auth';
import { provideCountry } from 'shared/core/data-access/country';
import {
    FALLBACK_CURRENCY_CODE,
    provideCurrency,
} from 'shared/core/data-access/currency';
import { provideSentry } from 'shared/core/data-access/error';
import { provideAssets } from 'shared/core/data-access/file';
import { LogLevelEnum, provideLogger } from 'shared/core/data-access/log';
import { provideMap } from 'shared/core/data-access/map';
import { provideSpeedUnit } from 'shared/core/data-access/speed-unit';
import { provideStorage } from 'shared/core/data-access/storage';
import { provideVersion } from 'shared/core/data-access/version';
import {
    provideLogo,
    provideTheming,
    provideWatermark,
} from 'shared/design-system/data-access/theme';
import {
    AbstractTemporaryFileService,
    provideTemporaryFileService,
} from 'shared/form/data-access/form';
import {
    LocationStrategyEnum,
    provideLocationStrategy,
} from 'shared/framework/data-access/angular';
import { BaseConfig } from '../../models/base-config/base-config.model';

export const createBaseAppConfig = (
    config: BaseConfig,
    routes: Routes,
    httpInterceptors: HttpInterceptorFn[],
    temporaryFileService: typeof AbstractTemporaryFileService,
    providers: ApplicationConfig['providers'],
): ApplicationConfig => ({
    providers: [
        provideApi(httpInterceptors),
        provideLocationStrategy(
            config.locationStrategy ?? LocationStrategyEnum.HASH,
        ),
        provideAuth({
            basePath: config.authBasePath,
            registrationId: config.authRegistrationId,
        }),
        provideAmplitude(config.amplitude),
        provideSentry({
            appName: config.appName,
            environment: config.environmentName,
            version: config.version,
            dsn: config.sentryDsn,
            debugToast: config.debugToast,
        }),
        provideLogger(config.logLevel ?? LogLevelEnum.ALL),
        provideStorage(config.storageKey),
        provideMap(config.mapKey),
        provideAssets({
            image: config.assetImagePath ?? './assets/images/',
        }),
        provideRouter(
            routes,
            withPreloading(PreloadAllModules),
            withComponentInputBinding(),
        ),
        provideVersion(config.version),
        provideTheming(config.defaultTheme),
        provideCurrency(config.currency ?? FALLBACK_CURRENCY_CODE),
        provideWatermark(config.watermark),
        provideLogo({
            logo: config.logo,
            darkLogo: config.darkLogo,
        }),
        provideSpeedUnit(config.speedUnit ?? $localize`:@@speed.kmh:km/h`),
        provideCountry(config.preferredCountryIds),
        provideTemporaryFileService(temporaryFileService),
        DialogService,
        MessageService,
        ...providers,
    ],
});
